import React, { useState } from 'react';
import {
    Card, Title, Accordion, Text, Group, Badge, Stack, SimpleGrid, Box, Paper, ThemeIcon, Divider, Button, Collapse
} from '@mantine/core';
import {
    IconPackage, IconWeight, IconRuler, IconCurrencyDollar, IconList, IconChevronDown
} from '@tabler/icons-react';
import { PackagesListProps } from './types';

const PackagesList: React.FC<PackagesListProps> = ({ packages }) => {
    const [expandedPackages, setExpandedPackages] = useState<Record<string, boolean>>({});

    const togglePackage = (packageId: string) => {
        setExpandedPackages(prev => ({
            ...prev,
            [packageId]: !prev[packageId]
        }));
    };

    const formatDimensions = (dimensions: { length: number; width: number; height: number }) => {
        return `${dimensions.length}" × ${dimensions.width}" × ${dimensions.height}"`;
    };

    const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

    return (
        <Card shadow="sm" p={{ base: "sm", sm: "lg" }} radius="md" withBorder>
            <Group justify="space-between" align="center" mb="md">
                <Title order={3} size="h3" lineClamp={1}>
                    Packages ({packages.length})
                </Title>
                <Badge size="lg" color="blue" variant="light">
                    {packages.reduce((total, pkg) => total + pkg.items.length, 0)} items total
                </Badge>
            </Group>

            {/* Mobile-First Layout */}
            <Box display={{ base: 'block', md: 'none' }}>
                <Stack gap="md">
                    {packages.map(pkg => (
                        <Paper key={pkg.id} withBorder radius="md" p="sm">
                            {/* Package Header */}
                            <Group justify="space-between" align="center" mb="sm">
                                <Group gap="xs" align="center">
                                    <ThemeIcon color="blue" variant="light" size="md" radius="xl">
                                        <IconPackage size={18} />
                                    </ThemeIcon>
                                    <Box>
                                        <Text fw={600} size="sm" lineClamp={1}>{pkg.name || `Package ${pkg.id}`}</Text>
                                        <Text size="xs" c="dimmed">ID: {pkg.id}</Text>
                                    </Box>
                                </Group>
                                <Badge size="sm" variant="light">{pkg.items.length} items</Badge>
                            </Group>

                            {/* Package Stats Grid */}
                            <SimpleGrid cols={2} spacing="xs" mb="sm">
                                <Paper bg="gray.0" p="xs" radius="sm">
                                    <Group gap="xs" align="center">
                                        <IconWeight size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Weight</Text>
                                            <Text size="xs" fw={500}>{pkg.weight} kg</Text>
                                        </Box>
                                    </Group>
                                </Paper>
                                <Paper bg="gray.0" p="xs" radius="sm">
                                    <Group gap="xs" align="center">
                                        <IconRuler size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Dimensions</Text>
                                            <Text size="xs" fw={500}>{formatDimensions(pkg.dimensions)}</Text>
                                        </Box>
                                    </Group>
                                </Paper>
                                <Paper bg="gray.0" p="xs" radius="sm">
                                    <Group gap="xs" align="center">
                                        <IconCurrencyDollar size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Value</Text>
                                            <Text size="xs" fw={500}>{formatCurrency(pkg.value)}</Text>
                                        </Box>
                                    </Group>
                                </Paper>
                                <Paper bg="gray.0" p="xs" radius="sm">
                                    <Group gap="xs" align="center">
                                        <IconList size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Description</Text>
                                            <Text size="xs" fw={500} lineClamp={1}>
                                                {pkg.description || 'No description'}
                                            </Text>
                                        </Box>
                                    </Group>
                                </Paper>
                            </SimpleGrid>

                            {/* Expandable Items List */}
                            <Button
                                variant="subtle"
                                size="xs"
                                fullWidth
                                onClick={() => togglePackage(pkg.id)}
                                rightSection={
                                    <IconChevronDown
                                        size={16}
                                        style={{
                                            transform: expandedPackages[pkg.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                }
                            >
                                {expandedPackages[pkg.id] ? 'Hide' : 'View'} Package Items
                            </Button>

                            <Collapse in={expandedPackages[pkg.id]}>
                                <Box mt="sm" pt="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                                    <Stack gap="xs">
                                        {pkg.items.map((item, itemIndex) => (
                                            <Paper key={itemIndex} bg="blue.0" p="xs" radius="sm">
                                                <Group justify="space-between" align="center">
                                                    <Box flex={1}>
                                                        <Text fw={500} size="xs" lineClamp={1}>{item.name}</Text>
                                                        <Text size="xs" c="dimmed">
                                                            Qty: {item.quantity} • {formatCurrency(Number(item.price))} each
                                                        </Text>
                                                    </Box>
                                                    <Text fw={600} size="xs" c="blue.7">
                                                        {formatCurrency(Number(item.price) * item.quantity)}
                                                    </Text>
                                                </Group>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            </Collapse>
                        </Paper>
                    ))}
                </Stack>
            </Box>

            {/* Desktop Layout */}
            <Box display={{ base: 'none', md: 'block' }}>
                <Accordion>
                    {packages.map(pkg => (
                        <Accordion.Item key={pkg.id} value={pkg.id}>
                            <Accordion.Control>
                                <Group gap="xs">
                                    <ThemeIcon color="blue" variant="light" size="lg" radius="xl">
                                        <IconPackage size={20} />
                                    </ThemeIcon>
                                    <div>
                                        <Text fw={500}>{pkg.name || `Package ${pkg.id}`}</Text>
                                        <Text size="xs" c="dimmed">ID: {pkg.id}</Text>
                                    </div>
                                    <Badge size="sm" ml="auto">{pkg.items.length} items</Badge>
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <SimpleGrid cols={2} spacing="md" mb="lg">
                                    <Box>
                                        <Text size="sm" c="dimmed" mb="xs">Package Details</Text>
                                        <Stack gap="xs">
                                            <Group justify="space-between">
                                                <Text size="sm">Weight</Text>
                                                <Text size="sm" fw={500}>{pkg.weight} kg</Text>
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Dimensions</Text>
                                                <Text size="sm" fw={500}>{formatDimensions(pkg.dimensions)}</Text>
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Total Value</Text>
                                                <Text size="sm" fw={500}>{formatCurrency(pkg.value)}</Text>
                                            </Group>
                                        </Stack>
                                    </Box>
                                    <Box>
                                        <Text size="sm" c="dimmed" mb="xs">Description</Text>
                                        <Text size="sm">{pkg.description || 'No description available'}</Text>
                                    </Box>
                                </SimpleGrid>

                                <Divider mb="md" />

                                <Text size="sm" c="dimmed" mb="sm">Items in this package:</Text>
                                <Stack gap="xs">
                                    {pkg.items.map((item, itemIndex) => (
                                        <Paper key={itemIndex} bg="gray.0" p="sm" radius="md">
                                            <Group justify="space-between" align="center">
                                                <Box>
                                                    <Text fw={500} size="sm">{item.name}</Text>
                                                    <Group gap="md">
                                                        <Text size="xs" c="dimmed">Quantity: {item.quantity}</Text>
                                                        <Text size="xs" c="dimmed">Unit Price: {formatCurrency(item.price)}</Text>
                                                        {item.category && (
                                                            <Badge size="xs" variant="outline">{item.category}</Badge>
                                                        )}
                                                    </Group>
                                                    {item.description && (
                                                        <Text size="xs" c="dimmed" mt="xs">{item.description}</Text>
                                                    )}
                                                </Box>
                                                <Text fw={600} size="md" c="blue.7">
                                                    {formatCurrency(Number(item.price) * item.quantity)}
                                                </Text>
                                            </Group>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </Box>
        </Card>
    );
};

export default PackagesList;
